/* eslint-disable indent */
import type { DesignGroup } from '../types/storeReviewOrderStep.types';
import type { StoreAiPreviewVibe } from '../constants/storeCheckoutAi.constants';
import { buildDesignImagesFromItem } from './buildDesignImagesFromItem.utils';
import { dedupeIdenticalDesignImageThumbnails } from './dedupeDesignImageThumbnails.utils';
import type { ICartItemDetail, IDesignImageThumb } from '@/common/interfaces/cart.interface';

export interface IProgressiveStyleSlots {
  front: string;
  back: string;
  design: string;
}

const isDesignImageType = (type: string): boolean => (type || '').toLowerCase().includes('design');

const resolveProgressiveSlotForDesignImageType = (
  type: string,
  slots: IProgressiveStyleSlots
): string | null => {
  const t = (type || '').toLowerCase();
  if (t === 'front') {
    return slots.front;
  }
  if (t === 'back') {
    return slots.back;
  }
  if (t.includes('design')) {
    return slots.design;
  }
  return null;
};

export const mergeDesignImagesWithProgressiveSlots = (
  images: IDesignImageThumb[],
  slots: IProgressiveStyleSlots
): IDesignImageThumb[] =>
  images.map((img) => {
    const nextSrc = resolveProgressiveSlotForDesignImageType(img.type, slots);
    return nextSrc ? { ...img, src: nextSrc } : { ...img };
  });

/** Single-image generation: replace garment preview only; keep design slot(s); drop back. */
export const mergeDesignImagesForSingleImageSlot = (
  images: IDesignImageThumb[],
  previewSrc: string
): IDesignImageThumb[] => {
  const designImages = images.filter((img) => isDesignImageType(img.type));
  const garmentWasSelected = images.some((img) => !isDesignImageType(img.type) && img.selected);
  const previewSelected = garmentWasSelected || !designImages.some((img) => img.selected);

  const previewImage: IDesignImageThumb = {
    src: previewSrc,
    type: 'front',
    selected: previewSelected,
  };

  const normalizedDesigns = designImages.map((img) => ({
    ...img,
    selected: previewSelected ? false : img.selected,
  }));

  return [previewImage, ...normalizedDesigns];
};

export const applyProgressiveStyleSlotsToDesignGroups = (
  groups: DesignGroup[],
  targetOrderItemId: number,
  slots: IProgressiveStyleSlots,
  previewImagesFromAi: StoreAiPreviewVibe | null = null,
  singleImageMode = false
): DesignGroup[] =>
  groups.map((group) => ({
    ...group,
    items: group.items.map((item) => {
      if (item.id !== targetOrderItemId) {
        return item;
      }
      const baseImages = buildDesignImagesFromItem(item);
      const nextImages = dedupeIdenticalDesignImageThumbnails(
        singleImageMode
          ? mergeDesignImagesForSingleImageSlot(baseImages, slots.front)
          : mergeDesignImagesWithProgressiveSlots(baseImages, slots).map((image) => ({
              ...image,
              selected: image.type === 'front',
            }))
      );

      if (singleImageMode) {
        const nextItem: ICartItemDetail = {
          ...item,
          designImages: nextImages,
        };
        if (previewImagesFromAi === 'ai-flat-lay' || previewImagesFromAi === 'ai-model') {
          nextItem.previewImagesFromAi = previewImagesFromAi;
        }
        return nextItem;
      }

      const nextItem: ICartItemDetail = {
        ...item,
        primaryImage: slots.front,
        frontImage: slots.front,
        backImage: slots.back,
        designImgPrimary: slots.design,
        designImages: nextImages,
      };
      if (previewImagesFromAi === 'ai-flat-lay' || previewImagesFromAi === 'ai-model') {
        nextItem.previewImagesFromAi = previewImagesFromAi;
      }
      return nextItem;
    }),
  }));

export const getReviewOrderItemId = (item: ICartItemDetail): number => item.id;
